import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api, { logFoodAPI } from '../../utils/api';
import TabStrip from '../../components/logfood/TabStrip';
import FoodsTab from './tabs/FoodsTab';
import RestaurantsTab from './tabs/RestaurantsTab';
import MealsTab from './tabs/MealsTab';
import BarcodeTab from './tabs/BarcodeTab';
import ManualTab from './tabs/ManualTab';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import { Lock, Crown } from 'lucide-react';

const FATSECRET_ENABLED = process.env.REACT_APP_ENABLE_FATSECRET === 'true';

/**
 * Inline lock shown to FREE users on Pro-only Log Food tabs (restaurants,
 * barcode). The backend still enforces the gate (HTTP 402) — this is the
 * friendly client surface that routes the user to the paywall.
 */
function ProTabLock({ label, description, onUpgrade }) {
  return (
    <div className="flex flex-col items-center text-center px-8 py-12" data-testid="pro-tab-lock">
      <span className="size-14 rounded-full bg-accent/15 flex items-center justify-center">
        <Lock className="size-6 text-accent" />
      </span>
      <h2 className="mt-4 text-base font-semibold text-foreground">{label} is a Fitxess Pro feature</h2>
      <p className="mt-1 text-sm text-muted-foreground max-w-xs">{description}</p>
      <button
        type="button"
        onClick={onUpgrade}
        className="mt-6 h-11 px-6 rounded-xl bg-gradient-to-r from-accent to-cyan-500 text-white text-sm font-semibold flex items-center gap-2"
        data-testid="pro-tab-lock-upgrade"
      >
        <Crown className="size-4" /> Upgrade to Fitxess Pro
      </button>
    </div>
  );
}

/**
 * LogFoodHub — main 5-tab Log Food experience.
 *
 * Responsibilities:
 *   - Render the horizontal tab strip with FatSecret-dependent tabs hidden
 *     when REACT_APP_ENABLE_FATSECRET=false
 *   - Pre-fetch the active tab's recents and pass them down
 *   - Centralized quick-log handler (toast confirmation, no FoodConfirm)
 *   - Route tab→tab transitions when fallback chips are tapped
 *   - Forward food selections up to the parent LogFood.jsx shell
 *
 * Props:
 *   date: YYYY-MM-DD string (the day being logged to)
 *   mealType: 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack'
 *   onClose: () => void — back button
 *   onSelectFood(food): user picked a SearchItem/RecentFood to review
 *   onOpenChain(chain): user tapped a Restaurants chain row
 *   onOpenManualEntry(prefillName?): open the existing manual-entry flow
 */
export default function LogFoodHub({
  date,
  mealType,
  onClose,
  onSelectFood,
  onAddToBasket,
  onSelectRecent,
  onQuickLogRecent,
  onOpenChain,
  onOpenManualEntry,
}) {
  const [activeTab, setActiveTab] = useState('foods');
  const [recents, setRecents] = useState([]);
  const [recentsLoading, setRecentsLoading] = useState(true);
  const { isFree, isSubscribed, showPaywall } = useSubscriptionContext();
  // AI photo-log entry point. The backend /ai-log/availability response drives
  // what we show:
  //   available:true                 -> Pro/enrolled user: tile opens the AI flow
  //   reason:'pro_required'          -> Free user (broad release): tile shown as
  //                                     a Pro upsell that opens the paywall
  //   feature_disabled/not_enrolled/ -> tile hidden (kill-switch off or the user
  //   login_required                   isn't part of the controlled rollout)
  // The backend remains authoritative on the protected /draft, /jobs, /commit
  // endpoints — this tile is only discovery/upsell.
  const [aiLog, setAiLog] = useState({ available: false, reason: null });
  const navigate = useNavigate();

  // Re-check on mount and whenever subscription state changes so a user who
  // upgrades from this paywall gets Pro access immediately without leaving
  // and reopening Log Food.
  useEffect(function () {
    let alive = true;
    api.get('/ai-log/availability')
      .then(function (r) { if (alive) setAiLog((r && r.data) || { available: false, reason: null }); })
      .catch(function () { if (alive) setAiLog({ available: false, reason: null }); });
    return function () { alive = false; };
  }, [isSubscribed]);
  const aiProUpsell = !aiLog.available && aiLog.reason === 'pro_required';
  const showAiTile = aiLog.available || aiProUpsell;

  const VISIBLE_TABS = [
    FATSECRET_ENABLED ? 'foods' : null,
    FATSECRET_ENABLED ? 'restaurants' : null,
    'meals',
    FATSECRET_ENABLED ? 'barcode' : null,
    'manual',
  ].filter(Boolean);

  // If the saved active tab is hidden by the flag, fall back to the first visible.
  useEffect(function () {
    if (!VISIBLE_TABS.includes(activeTab)) {
      setActiveTab(VISIBLE_TABS[0] || 'meals');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the recents for the current tab whenever it changes.
  useEffect(function () {
    setRecentsLoading(true);
    logFoodAPI.getRecentFoods({ scope: activeTab, limit: 20 })
      .then(function (res) {
        setRecents(Array.isArray(res.data) ? res.data : []);
        setRecentsLoading(false);
      })
      .catch(function () {
        setRecents([]);
        setRecentsLoading(false);
      });
  }, [activeTab]);

  // Quick re-log: call /log directly with previous qty (silently). Toast on success.
  //
  // Two shapes flow into here:
  //  • RecentFood (from /recent-foods): `macros` is the FULL scaled total
  //    from the most recent log (e.g. 390 cal for the user's 200 g chicken),
  //    `quantity_logged` is the qty that produced it, `serving_size` is the
  //    food's natural serving (e.g. 100 g).
  //  • FoodConfirm food (passed in from other tap-flows): `nutrition` is
  //    already per-unit and `serving_size` is the qty to send.
  //
  // The backend's /log endpoint computes `scaled = nutrition × quantity`,
  // so we must always send per-unit nutrition + the actual quantity. The
  // previous version sent FULL scaled macros AND qty = serving_size,
  // which silently multiplied macros by 100× on every FatSecret recent
  // re-log. This rebuild fixes that and also honours the user's last
  // logged quantity (the user's stated [+] behaviour for option A).
  const handleQuickLog = useCallback(function (item) {
    if (!item) return;
    const isRecentShape = !item.nutrition && !!item.macros;
    // Quantity to log: the user's last actual qty if we know it, else the
    // food's natural serving, else 1 (manual/legacy fallback).
    const qty = (typeof item.quantity_logged === 'number' && item.quantity_logged > 0)
      ? item.quantity_logged
      : (typeof item.serving_size === 'number' && item.serving_size > 0
          ? item.serving_size
          : 1);
    let perUnitNutrition;
    if (isRecentShape) {
      // RecentFood: macros are the FULL last-log totals. Divide by the
      // qty that produced them so backend.scaled = perUnit × qty restores
      // the original totals exactly.
      const m = item.macros || {};
      const divisor = qty > 0 ? qty : 1;
      perUnitNutrition = {
        calories: Number(m.calories || 0) / divisor,
        protein_g: Number(m.protein || 0) / divisor,
        carbs_g: Number(m.carbs || 0) / divisor,
        fat_g: Number(m.fat || 0) / divisor,
      };
    } else {
      // FoodConfirm-shape: nutrition is already per-unit.
      const n = item.nutrition || {};
      perUnitNutrition = {
        calories: Number(n.calories || 0),
        protein_g: Number(n.protein_g || 0),
        carbs_g: Number(n.carbs_g || 0),
        fat_g: Number(n.fat_g || 0),
      };
    }
    const foodPayload = {
      external_id: item.external_id || ('manual_' + Date.now()),
      name: item.name || 'Custom Food',
      brand: item.brand || null,
      restaurant: item.restaurant || null,
      image_url: item.image_url || null,
      serving_size: typeof item.serving_size === 'number' ? item.serving_size : 1,
      serving_unit: item.serving_unit || 'serving',
      serving_description: item.serving_description || null,
      nutrition: perUnitNutrition,
      source: item.source || 'CUSTOM',
      ingredients: [],
      serving_options: [],
    };
    logFoodAPI.logFood(foodPayload, date, mealType, qty)
      .then(function () {
        toast.success('Logged ' + foodPayload.name);
      })
      .catch(function () {
        toast.error('Failed to log. Please try again.');
      });
  }, [date, mealType]);

  // Fallback chip handler — switch to the suggested tab (search context cleared).
  const handleTryTab = useCallback(function (tabKey) {
    if (VISIBLE_TABS.includes(tabKey)) {
      setActiveTab(tabKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Search all recents →" — clear the active tab's query so the user
  // drops back to the full unfiltered top-20 recents for that scope. The
  // toast-only placeholder shipped earlier confused users into thinking
  // a dedicated page was promised; clearing the query mirrors what they
  // could do manually via the input's X button, but as a single tap from
  // the filtered-recents subsection.
  const handleSearchAllRecents = useCallback(function () {
    // The actual `setQuery('')` lives inside each tab — `_q` is unused
    // intentionally. We expose this stub so tab buttons can wire to a
    // single shared callback if we ever centralise the query state.
  }, []);

  const sharedTabProps = {
    recents: recents,
    recentsLoading: recentsLoading,
    onSelectFood: onSelectFood,
    // Recents are handled by LogFood (which can route GROUPED recents to
    // CustomMealView and convert normal recents to the per-unit FoodConfirm
    // shape). Search-result rows still use onQuickLog with a converted food.
    onSelectRecent: onSelectRecent,
    onQuickLogRecent: onQuickLogRecent,
    // The `+` on SEARCH rows adds to the multi-select basket when a basket
    // handler is provided; falls back to the legacy instant quick-log otherwise.
    onQuickLog: onAddToBasket || handleQuickLog,
    onTryTab: handleTryTab,
    onCreateManual: onOpenManualEntry,
    onSearchAllRecents: handleSearchAllRecents,
  };

  return (
    <div className="min-h-screen bg-background" data-testid="log-food-hub">
      <header className="flex items-center gap-2 px-2 pb-3 border-b border-border bg-background sticky top-0 z-10 header-safe-top">
        <button
          type="button"
          onClick={onClose}
          className="size-9 rounded-full hover:bg-accent flex items-center justify-center"
          aria-label="Back"
          data-testid="log-food-hub-back"
        >
          <ArrowLeft className="size-5" />
        </button>
        <h1 className="flex-1 text-base font-semibold text-foreground">Log Food</h1>
      </header>

      {showAiTile && (
        <button
          type="button"
          onClick={aiLog.available
            ? function () { navigate('/ai-log'); }
            : function () { showPaywall('ai_photo_logging'); }}
          className="mx-3 mt-3 w-[calc(100%-1.5rem)] flex items-center gap-3 rounded-xl border border-accent/30 bg-accent/10 px-3.5 py-3 text-left hover:bg-accent/15 transition-colors"
          data-testid="log-food-photo-tile"
        >
          <span className="size-9 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
            <Sparkles className="size-4 text-accent" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-foreground">Log with a photo</span>
            <span className="block text-xs text-muted-foreground">Snap your meal — AI drafts the estimate, you confirm</span>
          </span>
          {aiProUpsell && (
            <span
              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-accent/20 px-2 py-0.5 text-[11px] font-semibold text-accent"
              data-testid="log-food-photo-pro-badge"
            >
              <Crown className="size-3" /> Pro
            </span>
          )}
        </button>
      )}

      <TabStrip
        activeTab={activeTab}
        onChange={setActiveTab}
        visibleTabs={VISIBLE_TABS}
      />

      {activeTab === 'foods' && (
        <FoodsTab {...sharedTabProps} />
      )}
      {activeTab === 'restaurants' && (
        isFree ? (
          <ProTabLock
            label="Restaurant logging"
            description="Search restaurant chains and log menu items with accurate macros."
            onUpgrade={() => showPaywall('can_access_food_nearby')}
          />
        ) : (
          <RestaurantsTab {...sharedTabProps} onOpenChain={onOpenChain} />
        )
      )}
      {activeTab === 'meals' && (
        <MealsTab {...sharedTabProps} date={date} mealType={mealType} />
      )}
      {activeTab === 'barcode' && (
        isFree ? (
          <ProTabLock
            label="Barcode scanning"
            description="Scan a product barcode to log packaged foods instantly."
            onUpgrade={() => showPaywall('can_access_food_nearby')}
          />
        ) : (
          <BarcodeTab {...sharedTabProps} />
        )
      )}
      {activeTab === 'manual' && (
        <ManualTab {...sharedTabProps} />
      )}
    </div>
  );
}
